import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchOftenOrderedWith,
  fetchPublicMenu,
  fetchRecommendations,
  fetchTableByNumber,
  fetchTableInfo,
  placeOrderWithRetry,
  resolveImageUrl,
  cloudImg,
} from "../services/api";
import {
  newClientOrderId,
  readMenuCache,
  useOnline,
  writeMenuCache,
} from "../utils/offline";
import { formatPrice } from "../utils/currency";
import {
  COUNTRY_DEFAULTS,
  LABELS,
  TAG_LABELS,
  TAG_ORDER,
  makeT,
} from "../i18n/strings";
import OfflineBanner from "../components/OfflineBanner";
import LanguagePicker from "../components/LanguagePicker";
import MenuItemCard from "../components/MenuItemCard";
import CartPanel from "../components/CartPanel";
import RecommendationStrips from "../components/RecommendationStrips";

export default function MenuPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const restaurantId = params.get("restaurant_id");
  // The customer app accepts either form for the table param:
  //   ?table_id=N        - internal PK; legacy QR codes still work.
  //   ?table_number=N    - human-readable; preferred for new URLs.
  const tableIdParam = params.get("table_id");
  const tableNumberParam = params.get("table_number");

  const [tableInfo, setTableInfo] = useState(null);
  const [menu, setMenu] = useState([]);
  // Two separate recommendation streams returned by /public/recommendations:
  //   chefPicks   -> "Recommended by Chef" / "Today's Special" strip
  //                  (manual curation by chef/manager; carries chef_pick_note)
  //   topSellers  -> "Loved by our customers" strip
  //                  (auto-derived from order volume in last 30 days)
  // Each strip hides when its array is empty. A brand-new restaurant
  // with neither chef picks nor recent orders shows neither strip.
  const [chefPicks, setChefPicks] = useState([]);
  const [topSellers, setTopSellers] = useState([]);
  // "Often ordered with these" suggestions for the cart drawer.
  // Fetched (debounced) whenever the cart contents change so the strip
  // updates as the guest adds/removes items. Hidden when empty.
  const [oftenWith, setOftenWith] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // True while placeOrderWithRetry is between attempts (network blip).
  const [retrying, setRetrying] = useState(false);
  const online = useOnline();
  const [lang, setLang] = useState(() => localStorage.getItem("menulink_lang") || "en");
  useEffect(() => { localStorage.setItem("menulink_lang", lang); }, [lang]);

  // Active dietary-tag filters. When a tag is selected, only items whose
  // dietary_tags include that slug are shown. Multiple filters AND together
  // ("vegan AND gluten-free" → must have both). Kept in component state
  // only -- filters reset per page load by design (guests shouldn't get a
  // surprise "still filtering" experience next visit).
  const [tagFilters, setTagFilters] = useState(new Set());
  const toggleTag = (slug) => {
    setTagFilters((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const pickName = (item) => {
    if (lang === "am" && item.name_am) return item.name_am;
    if (lang === "om" && item.name_om) return item.name_om;
    if (lang === "sid" && item.name_sid) return item.name_sid;
    if (lang === "ti" && item.name_ti) return item.name_ti;
    if (lang === "nl" && item.name_nl) return item.name_nl;
    return item.name;
  };

  // Stable add callback so memoized recommendation cards keep their
  // identity across renders and only re-render when THEIR own count
  // changes — not when any other item is added. This is what makes
  // tapping "+ Add" on a rec card feel instant on low-end phones in
  // the field (where re-rendering all 50+ menu items and 6 rec cards
  // on every tap was the bottleneck).
  //
  // IMPORTANT — cart entry shape: the existing addOne/removeOne
  // functions below store entries as `{ item, quantity, modifiers,
  // note }` with the menu item NESTED under the `item` key. The
  // cart drawer + bar + submit payload all expect `c.item.price`,
  // `c.item.id`, etc. We MUST use the same shape here, otherwise
  // adding from a rec card creates a malformed entry and the next
  // render crashes the page (white screen). Mirror addOne exactly.
  const addItemToCart = useCallback((item) => {
    setCart((c) => {
      const cur = c[item.id] || { item, quantity: 0 };
      return { ...c, [item.id]: { ...cur, quantity: cur.quantity + 1 } };
    });
  }, []);
  const pickDescription = (item) => {
    if (lang === "nl" && item.description_nl) return item.description_nl;
    return item.description;
  };

  // The language picker is admin-controlled per tenant. The backend
  // returns ``supported_languages`` as an explicit list (e.g. for
  // Sintalle Hawassa: ["en","am","sid"]); when null, fall back to the
  // legacy country-based defaults so unchanged tenants keep working.
  //
  // Display labels are kept consistent regardless of the order — admin
  // ticks language checkboxes in TenantSettingsPage, customer sees them
  // in the picker in the order returned.
  const country = (tableInfo?.country || "OTHER").toUpperCase();
  // Per-tenant currency from /restaurant-info. Falls back to ETB so
  // legacy responses (pre-currency-column) still render reasonable prices.
  const currency = tableInfo?.currency || "ETB";
  const explicit = Array.isArray(tableInfo?.supported_languages)
    ? tableInfo.supported_languages
    : null;
  const langCodes = (explicit && explicit.length > 0)
    ? explicit
    : (COUNTRY_DEFAULTS[country] || COUNTRY_DEFAULTS.OTHER);
  // Map codes -> {code,label}; drop unknown codes defensively (so a
  // backend that returns a code the frontend doesn't recognise yet
  // doesn't crash the picker — it just hides that one option).
  const langOptions = langCodes
    .map((c) => LABELS[c])
    .filter(Boolean);
  // Always guarantee at least English so the page never renders with
  // zero language options.
  if (langOptions.length === 0) langOptions.push(LABELS.en);
  // Coerce the persisted lang if it isn't valid for this restaurant's country.
  useEffect(() => {
    if (!langOptions.some((o) => o.code === lang)) {
      setLang(langOptions[0].code);
    }
  }, [country]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!restaurantId || (!tableIdParam && !tableNumberParam)) {
      setError(
        "Missing restaurant_id and either table_id or table_number in the URL."
      );
      setLoading(false);
      return;
    }
    // Offline resilience: render the last cached menu instantly (if any),
    // then revalidate from the network in the background. On a flaky
    // connection the guest still sees the menu they scanned earlier.
    const cached = readMenuCache(restaurantId);
    if (cached) {
      setMenu(cached.menu);
      setChefPicks(cached.chefPicks || []);
      setTopSellers(cached.topSellers || []);
      setLoading(false);
    }
    (async () => {
      try {
        const tablePromise = tableNumberParam
          ? fetchTableByNumber(restaurantId, tableNumberParam)
          : fetchTableInfo(tableIdParam);
        // Fetch recommendations in parallel — if it errors out (older
        // backend without the endpoint), don't block the menu load.
        const [info, items, recs] = await Promise.all([
          tablePromise,
          fetchPublicMenu(restaurantId),
          fetchRecommendations(restaurantId).catch(() => ({
            chef_picks: [], top_sellers: [],
          })),
        ]);
        setTableInfo(info);
        setMenu(items);
        // The recommendations endpoint returns
        //   { chef_picks: [...], top_sellers: [...] }
        // Defensive: if an older response shape arrives (just an array),
        // treat the whole thing as top_sellers so customers still see
        // something useful instead of two empty strips.
        if (recs && Array.isArray(recs.chef_picks)) {
          setChefPicks(recs.chef_picks);
          setTopSellers(Array.isArray(recs.top_sellers) ? recs.top_sellers : []);
        } else if (Array.isArray(recs)) {
          setChefPicks([]);
          setTopSellers(recs);
        } else {
          setChefPicks([]);
          setTopSellers([]);
        }
        // Save for the next visit / the next network blip.
        writeMenuCache(restaurantId, {
          menu: items,
          chefPicks: Array.isArray(recs?.chef_picks) ? recs.chef_picks : [],
          topSellers: Array.isArray(recs?.top_sellers) ? recs.top_sellers : [],
        });
      } catch (err) {
        console.error(err);
        // If we rendered a cached menu, stay on it quietly — the guest
        // can still browse and order; retry logic protects the POST.
        if (!cached) {
          setError(
            err?.response?.data?.detail || "Could not load menu. Is the QR code still valid?"
          );
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [restaurantId, tableIdParam, tableNumberParam]);

  // ----- "Often ordered with these" -----
  // Refetch suggestions whenever the cart's item-id set changes. Debounced
  // by 400ms so rapid +/- taps don't hammer the API. Failures are quiet
  // — the strip just stays empty and hides itself.
  const cartIdsKey = Object.keys(cart).sort().join(",");
  useEffect(() => {
    if (!restaurantId) return;
    const ids = Object.keys(cart).map((x) => parseInt(x, 10)).filter(Boolean);
    if (ids.length === 0) {
      setOftenWith([]);
      return;
    }
    let cancel = false;
    const timer = setTimeout(() => {
      fetchOftenOrderedWith(restaurantId, ids)
        .then((data) => {
          if (cancel) return;
          setOftenWith(Array.isArray(data) ? data : []);
        })
        .catch(() => {
          if (!cancel) setOftenWith([]);
        });
    }, 400);
    return () => { cancel = true; clearTimeout(timer); };
  }, [restaurantId, cartIdsKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Apply dietary filters before grouping. AND semantics: an item passes
  // only when its dietary_tags include EVERY currently-selected slug.
  const filteredMenu = useMemo(() => {
    if (tagFilters.size === 0) return menu;
    return menu.filter((it) => {
      const tags = new Set(it.dietary_tags || []);
      for (const need of tagFilters) {
        if (!tags.has(need)) return false;
      }
      return true;
    });
  }, [menu, tagFilters]);

  const grouped = useMemo(() => {
    const g = {};
    filteredMenu.forEach((item) => {
      const cat = item.category || "Other";
      if (!g[cat]) g[cat] = [];
      g[cat].push(item);
    });
    return g;
  }, [filteredMenu]);

  // Only show filter chips for tags that at least one item actually has,
  // so a restaurant with no vegan dishes doesn't show a misleading
  // "vegan" filter. Computed against the unfiltered menu so the chip set
  // doesn't shrink as filters are applied.
  const availableTags = useMemo(() => {
    const set = new Set();
    menu.forEach((it) => (it.dietary_tags || []).forEach((t) => set.add(t)));
    return TAG_ORDER.filter((t) => set.has(t));
  }, [menu]);

  const cartItems = Object.values(cart);
  const total = cartItems.reduce((sum, c) => {
    const surcharge = (c.modifiers || []).reduce((a, m) => a + (m.price || 0), 0);
    return sum + (c.item.price + surcharge) * c.quantity;
  }, 0);
  const itemCount = cartItems.reduce((n, c) => n + c.quantity, 0);

  const addOne = (item) =>
    setCart((c) => {
      const cur = c[item.id] || { item, quantity: 0 };
      return { ...c, [item.id]: { ...cur, quantity: cur.quantity + 1 } };
    });
  const removeOne = (item) =>
    setCart((c) => {
      const cur = c[item.id];
      if (!cur) return c;
      const q = cur.quantity - 1;
      const next = { ...c };
      if (q <= 0) delete next[item.id];
      else next[item.id] = { ...cur, quantity: q };
      return next;
    });

  const setItemNote = (item, note) =>
    setCart((c) => {
      const cur = c[item.id];
      if (!cur) return c;
      return { ...c, [item.id]: { ...cur, note } };
    });

  const toggleModifier = (item, mod) =>
    setCart((c) => {
      const cur = c[item.id];
      if (!cur) return c;
      const selected = cur.modifiers || [];
      const exists = selected.find((m) => m.name === mod.name);
      const next = exists
        ? selected.filter((m) => m.name !== mod.name)
        : [...selected, mod];
      return { ...c, [item.id]: { ...cur, modifiers: next } };
    });

  const submit = async () => {
    if (!tableInfo?.table_id) {
      setError("Table not resolved. Please re-scan the QR code.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        // Always use the resolved internal table_id from the lookup,
        // regardless of which URL form (table_id vs table_number) brought
        // us here. The order POST endpoint takes the internal PK.
        tableId: Number(tableInfo.table_id),
        notes: notes || undefined,
        items: cartItems.map((c) => ({
          menu_id: c.item.id,
          quantity: c.quantity,
          notes: c.note || undefined,
          modifiers: c.modifiers && c.modifiers.length ? c.modifiers : undefined,
        })),
      };
      // Idempotency key: every retry of THIS submit sends the same id,
      // so the kitchen can never receive the order twice.
      payload.clientOrderId = newClientOrderId();
      const order = await placeOrderWithRetry(payload, () => setRetrying(true));
      // Navigate with the order's unguessable public_token (not its sequential
      // id) so the confirm / receipt / rating links can't be enumerated.
      navigate(`/order/confirm/${order.public_token}`, {
        state: { order, tableInfo },
      });
    } catch (err) {
      console.error(err);
      const t_ = makeT(lang);
      let msg;
      if (!err.response) {
        msg = t_("order_network_fail");
      } else {
        const d = err.response.data && err.response.data.detail;
        if (typeof d === "string") {
          msg = d;
        } else if (Array.isArray(d) && d.length) {
          // FastAPI 422 validation: a list of error objects (never render raw).
          const notesTooLong = d.some((e) => Array.isArray(e.loc) && e.loc.includes("notes"));
          msg = notesTooLong ? "Your note is too long — please shorten it." : (d[0].msg || "Could not submit your order.");
        } else if (d && typeof d === "object") {
          msg = d.message || d.msg || "Could not submit your order.";
        } else {
          msg = "Could not submit your order.";
        }
      }
      setError(msg);
    } finally {
      setSubmitting(false);
      setRetrying(false);
    }
  };

  const t = makeT(lang);

  if (loading) return <div className="page center">{t("loading_menu")}</div>;
  if (error) {
    return (
      <div className="page">
        <div className="notice">{error}</div>
      </div>
    );
  }

  // Resolve the restaurant's own logo if they're on Business tier and have
  // uploaded one. Falls back to the generic ODRA mark so the layout is
  // identical for non-Business tenants.
  const customLogo = tableInfo?.logo_url ? cloudImg(tableInfo.logo_url, 120) : null;
  const customCover = tableInfo?.cover_image_url ? cloudImg(tableInfo.cover_image_url, 1200) : null;

  return (
    <>
      <OfflineBanner online={online} t={t} />
      {customCover && (
        <div
          className="cover-hero"
          style={{
            position: "relative",
            width: "100%",
            aspectRatio: "3 / 1",
            maxHeight: 220,
            overflow: "hidden",
            borderRadius: 12,
            marginBottom: 12,
            background: `#1f2933 url(${customCover}) center / cover no-repeat`,
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)",
            }}
          />
          <div
            style={{
              position: "absolute", left: 14, bottom: 12,
              color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,0.6)",
            }}
          >
            <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>
              {tableInfo?.restaurant_name || "Menu"}
            </div>
            <div style={{ fontSize: ".85rem", opacity: 0.95 }}>
              Table {tableInfo?.table_number}
            </div>
          </div>
        </div>
      )}

      <div className="brand-bar" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <img
          src={customLogo || "/logo64.png"}
          alt=""
          width="36"
          height="36"
          style={{ borderRadius: 8, objectFit: "contain", background: "#fff" }}
        />
        <div style={{ flex: 1 }}>
          {!customCover && (
            <>
              <h1 style={{ margin: 0 }}>{tableInfo?.restaurant_name || "Menu"}</h1>
              <div className="sub">Table {tableInfo?.table_number}</div>
            </>
          )}
          {customCover && (
            <div className="sub" style={{ fontWeight: 600 }}>Menu</div>
          )}
        </div>
        <LanguagePicker langOptions={langOptions} lang={lang} setLang={setLang} />
      </div>

      <div className="page">
        {/* Chef picks + top sellers — see RecommendationStrips for the
            hide-on-empty / hide-when-filtering rules. */}
        <RecommendationStrips
          chefPicks={chefPicks}
          topSellers={topSellers}
          tagFilters={tagFilters}
          cart={cart}
          onAdd={addItemToCart}
          pickName={pickName}
          t={t}
          lang={lang}
          currency={currency}
        />

        {/* Dietary filter bar — only renders if there's at least one
            taggable item on the menu. AND semantics: stacking filters
            narrows the result set. */}
        {availableTags.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              marginBottom: 12,
            }}
            role="group"
            aria-label="Dietary filters"
          >
            {availableTags.map((slug) => {
              const opt = TAG_LABELS[slug];
              if (!opt) return null;
              const active = tagFilters.has(slug);
              const label = (opt[lang] || opt.en) + (opt.icon ? ` ${opt.icon}` : "");
              return (
                <button
                  key={slug}
                  type="button"
                  onClick={() => toggleTag(slug)}
                  aria-pressed={active}
                  style={{
                    border: active ? `1px solid ${opt.color}` : "1px solid #d3c8af",
                    background: active ? opt.color : "#fff",
                    color: active ? "#fff" : "#1f2933",
                    padding: "4px 12px",
                    borderRadius: 999,
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {label}
                </button>
              );
            })}
            {tagFilters.size > 0 && (
              <button
                type="button"
                onClick={() => setTagFilters(new Set())}
                style={{
                  border: "1px solid #d3c8af",
                  background: "transparent",
                  padding: "4px 12px",
                  borderRadius: 999,
                  fontSize: "0.82rem",
                  cursor: "pointer",
                }}
              >
                ✕ {lang === "nl" ? "Wis filters" : lang === "am" ? "ሁሉም" : lang === "om" ? "Haqi" : "Clear"}
              </button>
            )}
          </div>
        )}
        {Object.keys(grouped).length === 0 && (
          <div className="notice">
            {tagFilters.size > 0
              ? (lang === "nl"
                  ? "Geen gerechten passen bij deze filters."
                  : lang === "am"
                  ? "በዚህ ማጣሪያ ምንም ምግብ የለም።"
                  : lang === "om"
                  ? "Nyaata filtariwwan kanaan walsima hin jiru."
                  : "No items match these filters.")
              : t("no_items_yet")}
          </div>
        )}

        {Object.entries(grouped).map(([cat, items]) => (
          <section key={cat}>
            <h2 className="category">{cat}</h2>
            <div className="grid">
              {items.map((item) => (
                <MenuItemCard
                  key={item.id}
                  item={item}
                  inCart={cart[item.id]?.quantity || 0}
                  addOne={addOne}
                  removeOne={removeOne}
                  pickName={pickName}
                  pickDescription={pickDescription}
                  lang={lang}
                  t={t}
                  currency={currency}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {itemCount > 0 && (
        <div className="cart-bar">
          <span>
            {itemCount} item{itemCount === 1 ? "" : "s"}
          </span>
          <span className="total">{formatPrice(total, currency)}</span>
          <button className="btn" onClick={() => setCartOpen(true)}>
            Review &amp; Order
          </button>
        </div>
      )}

      <p className="privacy-note" style={{ textAlign: "center", fontSize: "0.72rem", color: "#8a8a8a", margin: "28px 12px 96px", lineHeight: 1.55 }}>
        Your order is shared only with this venue to prepare and serve it. ODRA processes it on the venue's behalf and does not sell your data.
        <br />
        ትዕዛዝዎ ለማዘጋጀትና ለማቅረብ ብቻ ለዚህ ተቋም ይጋራል። ODRA በተቋሙ ምትክ ያስኬዳል፤ መረጃዎን አይሸጥም።
      </p>

      {cartOpen && (
        <CartPanel
          cartItems={cartItems}
          total={total}
          tableInfo={tableInfo}
          oftenWith={oftenWith}
          notes={notes}
          setNotes={setNotes}
          error={error}
          submitting={submitting}
          retrying={retrying}
          submit={submit}
          onClose={() => setCartOpen(false)}
          addOne={addOne}
          removeOne={removeOne}
          toggleModifier={toggleModifier}
          setItemNote={setItemNote}
          addItemToCart={addItemToCart}
          pickName={pickName}
          lang={lang}
          t={t}
          currency={currency}
        />
      )}
    </>
  );
}

